'use client';

import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { IFormMetadataProps } from '@/components/Form/types';
import { Modal } from '@/components/Modal/Modal';
import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents, isFormDynamicsLocationEnabled, phoneRegex } from '@/tools/forms';
import { IComponentGenericFormField } from '@/types/cms';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping } from '@/types/dynamicsForms';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parsePhone } from '@lamesarv-sdk/tools';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { StyleClassicForm } from './Styles/Classic';
import { StyleSimpleForm } from './Styles/Simple';
import { IGenericInquiryFormProps } from './types';

import * as Yup from 'yup';

interface IInventoryForm extends IFormMetadataProps {
  leadType: string;
  leadSource: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  contact: string;
  product: string;
  comments: string;
  terms: boolean;
  subscribe?: boolean;
  smsOptIn?: boolean;
}

// --

const FormReset = ({ wasSuccessful }: { wasSuccessful: boolean }) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

const formCaptchaErrorMessage = () => {
  return (
    <div className="w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="w-full flex flex-col md:flex-row gap-1 md:gap-2 items-center justify-center">
        <span>Form validation failed.</span>
        <span className="flex flex-row gap-2 items-center">
          Please Call{' '}
          <a href="tel:877-641-8052" className="flex gap-2 items-center decoration-dashed underline underline-offset-4">
            <FontAwesomeIcon icon={faPhone} className="size-3.5" />
            <span>877-641-8052</span>
          </a>
        </span>
        <span>for your inquiry</span>
      </div>
    </div>
  );
};

// --

const getHashSanitized = (hash?: string) => {
  if (!hash || hash?.trim() === '#') return undefined;
  return hash.replaceAll('#', '').trim().toLowerCase();
};

const removeHash = () => {
  if (typeof window === 'undefined') return;
  if ('replaceState' in window.history) {
    window.history.replaceState('', window.document.title, window.location.pathname + window.location.search);
  }
};

// --

const getYupStringSchema = (item: IComponentGenericFormField, label: string) => {
  return item.required ? Yup.string().required(`${label} is required`) : Yup.string().optional();
};

const mapFormFields = (fieldsBase: IComponentGenericFormField[], includeTerms?: boolean) => {
  const fields: { [field: string]: IComponentGenericFormField } = {};
  const fieldsSchema: Record<string, Yup.AnySchema> = {};

  const fieldMapping: Record<string, { key: string; label: string }> = {
    'first-name': { key: 'firstName', label: 'First Name' },
    'last-name': { key: 'lastName', label: 'Last Name' },
    comments: { key: 'comments', label: 'Comments' },
    'preferred-location': { key: 'location', label: 'Location' },
    'preferred-contact': { key: 'contact', label: 'Contact' },
    product: { key: 'product', label: 'Product' },
    email: { key: 'email', label: 'Email' },
    phone: { key: 'phone', label: 'Phone number' },
  };

  fieldsBase.forEach((item) => {
    fields[item.type] = item;

    const fieldMapped = fieldMapping[item.type];

    if (fieldMapped) {
      if (item.type === 'email') {
        fieldsSchema[fieldMapped.key] = item.required
          ? Yup.string().email('Invalid email').required(`${fieldMapped.label} is required`)
          : Yup.string().email('Invalid email').optional();
      } else if (item.type === 'phone') {
        fieldsSchema[fieldMapped.key] = item.required
          ? Yup.string()
              .required(`${fieldMapped.label} is required`)
              .matches(phoneRegex, 'Please enter a valid phone number.')
          : Yup.string().optional();
      } else {
        fieldsSchema[fieldMapped.key] = getYupStringSchema(item, fieldMapped.label);
      }
    } else {
      fieldsSchema[item.type] = Yup.mixed().optional();
    }
  });

  if (includeTerms) {
    fieldsSchema.terms = Yup.boolean().oneOf([true], 'You must accept the terms and conditions');
  }

  return {
    fields,
    schema: Yup.object().shape(fieldsSchema),
  };
};

const mapStyles = {
  simple: StyleSimpleForm,
  classic: StyleClassicForm,
};

const FormBase = (props: IGenericInquiryFormProps) => {
  const { context } = useContext(LayoutContext);
  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const { fields, schema } = mapFormFields(props.fields, props.display_terms_checkbox);

  const onSubmit = async (data: IInventoryForm, metadata?: Record<string, unknown>) => {
    const mappings: IDynamicsFieldMapping[] = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue:
          props.custom_form_leadtype && props.custom_form_leadtype in DynamicsLeadType
            ? String(DynamicsLeadType[props.custom_form_leadtype as keyof typeof DynamicsLeadType])
            : String(DynamicsLeadType.seller),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue:
          props.custom_form_leadsource && props.custom_form_leadsource in DynamicsLeadSource
            ? String(DynamicsLeadSource[props.custom_form_leadsource as keyof typeof DynamicsLeadSource])
            : String(DynamicsLeadSource['website-online-inquiry']),
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: data.firstName || '' },
      { DataverseFieldName: 'lastname', DataverseFieldValue: data.lastName || '' },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: data.email || '' },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: data.phone ? parsePhone(data.phone) : '' },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue: String(
          props.locationsOptionSet.find((item) => item.Label === data.location)?.DataverseValue ?? '',
        ),
      },
      { DataverseFieldName: 'preferredcontactmethodcode', DataverseFieldValue: data.contact || '' },
      { DataverseFieldName: 'lmrv_product', DataverseFieldValue: data.product || '' },
      { DataverseFieldName: 'lmrv_comments', DataverseFieldValue: data.comments || '' },
      { DataverseFieldName: 'lmrv_accepttermsandconditions', DataverseFieldValue: data.terms ? '1' : '0' },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    if (data.subscribe || data.smsOptIn) {
      const topicChannels = [data.subscribe ? 'Email' : undefined, data.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    if (data.location && isFormDynamicsLocationEnabled(env, data.location)) {
      await sendForm({
        formKey: 'INQUIRY',
        formName: props.custom_name || 'Generic Inquiry Form',
        mappings,
        customDynamicsFormId: props.custom_dynamics_id,
      });
    }
  };

  const wasSuccessful = !isLoading && !hasError && submitted;
  const wasError = !isLoading && hasError;

  useEffect(() => {
    if (wasSuccessful && props.onSuccess) {
      props.onSuccess();
    }
  }, [wasSuccessful]);

  const FormStyled = mapStyles[props.style] ?? mapStyles.simple;

  return (
    <CommonForm
      id={props.custom_id || 'genericInquiryForm'}
      schema={schema}
      onSubmit={onSubmit}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-inquiry` : 'form-inquiry'}
      className={twMerge(
        'font-helvetica',
        props.modal || context.isEmbed ? '' : 'py-[40px] md:py-[50px]',
        props.classes_container,
      )}
      customCaptchaErrorMessage={formCaptchaErrorMessage}
    >
      <CommonHidden name="formId" value={props.custom_active_campaign_id || '23'} />
      <CommonHidden name="formType" value={props.custom_form_type || 'custom'} />
      <CommonHidden name="leadType" value={props.custom_form_leadtype || 'seller'} />
      <CommonHidden name="leadSource" value={props.custom_form_leadsource || 'lamesarv'} />
      <FormStyled
        {...props}
        fieldsMapped={fields}
        isDisabled={isLoading || wasSuccessful}
        wasError={wasError}
        wasSuccessful={wasSuccessful}
      />
      <FormReset wasSuccessful={wasSuccessful} />
    </CommonForm>
  );
};

export const GenericInquiryForm = (props: IGenericInquiryFormProps) => {
  const { searchParams } = useCustomSearchParams();
  const [modalOpen, setModalOpen] = useState(false);

  const hashParameter = getHashSanitized(props?.modal_hash_parameter);

  const onHashChanged = () => {
    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);

      if (hashParameter) {
        setModalOpen(hashLocation === hashParameter);
      }
    }
  };

  useEffect(() => {
    let modalOpenStatus: boolean = false;

    if (props.modal && props.modal_url_parameter && !!searchParams.get(props.modal_url_parameter)) {
      modalOpenStatus = true;
    }

    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);
      const hashParameter = getHashSanitized(props?.modal_hash_parameter);

      if (!!hashParameter && hashLocation === hashParameter) {
        modalOpenStatus = true;
      }
    }

    setModalOpen(modalOpenStatus);

    // Hash Change Control
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChanged);
      return () => {
        window.removeEventListener('hashchange', onHashChanged);
      };
    }
  }, []);

  const onClose = () => {
    setModalOpen(false);
    if (hashParameter) {
      removeHash();
    }
  };

  const onSuccess = () => {
    setTimeout(() => {
      setModalOpen(false);
      if (hashParameter) {
        removeHash();
      }
    }, 2000);
  };

  if (props.modal) {
    return (
      <Modal isOpen={modalOpen} onClose={onClose} useMaxWidth={false} classesClose="right-2 top-2 md:right-8 md:top-8">
        <FormBase
          {...props}
          onSuccess={onSuccess}
          classes={twMerge('md:w-full md:max-w-[720px] xl:w-full xl:max-w-[865px] border-none', props.classes)}
        />
      </Modal>
    );
  }

  return <FormBase {...props} />;
};
